import { useFrame, useThree } from '@react-three/fiber'
import { RefObject, useEffect, useRef } from 'react'
import {
    BufferAttribute, BufferGeometry,
    CanvasTexture, DoubleSide, Group, Mesh,
    MathUtils, MeshBasicMaterial, NearestFilter, NormalBlending, Quaternion, SRGBColorSpace, Vector3,
} from 'three'
import {
    type FootstepTimelineRef,
} from './footstepConfig'
import { store } from '@/src/stores/store'

const MAX_SAMPLES = 64
const VERTICES_PER_SAMPLE = 2
const FRONT_CAP_SEGMENTS = 18
const STRIPE_COLORS = [
    '#ff1744', '#ff9100', '#ffea00', '#76ff03',
    '#00e5ff', '#2979ff', '#7c4dff', '#f500ff',
] as const
const trailSettings = {
    width: 0.6,
    endWidth: 0.6,
    opacity: 1,
    sampleSpacing: 0.02,
    maxPoints: 32,
    trailLength: 12,
    offsetY: 0.02,
    offsetZ: -0.1,
    frontCapRadius: 0.3,
    positionSmoothing: 24,
    trailLengthSmoothing: 12,
}
const configuratorPreviewTrailLengthMultiplier = 0.127

const _worldPos = new Vector3()
const _smoothedPos = new Vector3()
const _targetPos = new Vector3()
const _trailOffset = new Vector3(0, trailSettings.offsetY, trailSettings.offsetZ)
const _prevSample = new Vector3()
const _pt = new Vector3()
const _prevPt = new Vector3()
const _nextPt = new Vector3()
const _segment = new Vector3()
const _dir = new Vector3()
const _hDir = new Vector3()
const _ribbonSide = new Vector3()
const _upVec = new Vector3(0, 1, 0)
const _trailWorldQuat = new Quaternion()
const _previewForward = new Vector3()
const _previewBackward = new Vector3()

type EffectRunGlowProps = {
    anchorRef: RefObject<Group | null>
    timelineRef: FootstepTimelineRef
}

type TrailSample = {
    distance: number
    position: Vector3
    up: Vector3
}

function createStripeTexture() {
    const canvas = document.createElement('canvas')
    canvas.width = STRIPE_COLORS.length
    canvas.height = 1
    const ctx = canvas.getContext('2d')!
    STRIPE_COLORS.forEach((color, i) => { ctx.fillStyle = color; ctx.fillRect(i, 0, 1, 1) })
    const texture = new CanvasTexture(canvas)
    texture.magFilter = NearestFilter
    texture.minFilter = NearestFilter
    texture.generateMipmaps = false
    texture.colorSpace = SRGBColorSpace
    return texture
}

export function EffectRunGlow({ anchorRef, timelineRef }: EffectRunGlowProps) {
    const { scene } = useThree()
    const trailPositions = useRef(new Float32Array(MAX_SAMPLES * VERTICES_PER_SAMPLE * 3))
    const trailUvs = useRef(new Float32Array(MAX_SAMPLES * VERTICES_PER_SAMPLE * 2))
    const trailGeomRef = useRef<BufferGeometry | null>(null)
    const trailMeshRef = useRef<Mesh | null>(null)
    const frontCapGeomRef = useRef<BufferGeometry | null>(null)
    const frontCapMeshRef = useRef<Mesh | null>(null)
    const frontCapPositions = useRef(new Float32Array((FRONT_CAP_SEGMENTS - 1) * 3 * 3))
    const frontCapUvs = useRef(new Float32Array((FRONT_CAP_SEGMENTS - 1) * 3 * 2))
    const samplesRef = useRef<TrailSample[]>([])
    const smoothedPosInitRef = useRef(false)
    const stripeTextureRef = useRef(createStripeTexture())
    const travelDistanceRef = useRef(0)
    const currentTrailLengthRef = useRef(0)

    useEffect(() => {
        const trailIndices: number[] = []
        for (let i = 0; i < MAX_SAMPLES - 1; i++) {
            const b = i * VERTICES_PER_SAMPLE
            const nb = (i + 1) * VERTICES_PER_SAMPLE
            trailIndices.push(b, b + 1, nb, b + 1, nb + 1, nb)
        }
        const trailGeom = new BufferGeometry()
        trailGeom.setAttribute('position', new BufferAttribute(trailPositions.current, 3))
        trailGeom.setAttribute('uv', new BufferAttribute(trailUvs.current, 2))
        trailGeom.setIndex(trailIndices)
        trailGeom.setDrawRange(0, 0)
        trailGeomRef.current = trailGeom

        const trailMat = new MeshBasicMaterial({
            color: '#ffffff',
            map: stripeTextureRef.current,
            transparent: true,
            opacity: trailSettings.opacity,
            depthWrite: false,
            blending: NormalBlending,
            side: DoubleSide,
            toneMapped: false,
        })
        trailMat.forceSinglePass = true
        const trailMesh = new Mesh(trailGeom, trailMat)
        trailMesh.frustumCulled = false
        trailMesh.visible = false
        trailMesh.renderOrder = -10
        scene.add(trailMesh)
        trailMeshRef.current = trailMesh

        const capGeom = new BufferGeometry()
        capGeom.setAttribute('position', new BufferAttribute(frontCapPositions.current, 3))
        capGeom.setAttribute('uv', new BufferAttribute(frontCapUvs.current, 2))
        frontCapGeomRef.current = capGeom

        const capMat = new MeshBasicMaterial({
            color: '#ffffff',
            map: stripeTextureRef.current,
            transparent: true,
            opacity: trailSettings.opacity,
            depthWrite: false,
            blending: NormalBlending,
            side: DoubleSide,
            toneMapped: false,
        })
        capMat.forceSinglePass = true
        const capMesh = new Mesh(capGeom, capMat)
        capMesh.frustumCulled = false
        capMesh.visible = false
        capMesh.renderOrder = -9
        scene.add(capMesh)
        frontCapMeshRef.current = capMesh

        return () => {
            scene.remove(trailMesh)
            trailGeom.dispose()
            trailMat.dispose()
            scene.remove(capMesh)
            capGeom.dispose()
            capMat.dispose()
            stripeTextureRef.current.dispose()
        }
    }, [scene])

    function syncRibbon() {
        const trailGeom = trailGeomRef.current
        const trailMesh = trailMeshRef.current
        const capGeom = frontCapGeomRef.current
        const capMesh = frontCapMeshRef.current
        if (!trailGeom || !trailMesh || !capGeom || !capMesh) return

        const samples = samplesRef.current
        const maxTrailLength = Math.max(currentTrailLengthRef.current, 0)
        let sampleCount = 0

        for (let i = 0; i < samples.length; i++) {
            const distanceFromHead = Math.max(0, samples[0].distance - samples[i].distance)
            sampleCount = i + 1
            if (distanceFromHead >= maxTrailLength) {
                break
            }
        }

        sampleCount = Math.min(sampleCount, Math.max(2, trailSettings.maxPoints))

        if (sampleCount < 2) {
            trailGeom.setDrawRange(0, 0)
            trailMesh.visible = false
            capMesh.visible = false
            return
        }

        const cumulativeDistances = new Float32Array(sampleCount)
        const headDistance = samples[0].distance
        for (let i = 0; i < sampleCount; i++) {
            cumulativeDistances[i] = Math.max(0, headDistance - samples[i].distance)
        }
        const safeTotalDistance = Math.max(cumulativeDistances[sampleCount - 1], 1e-6)

        for (let i = 0; i < sampleCount; i++) {
            _pt.copy(samples[i].position)
            _prevPt.copy(
                i > 0
                    ? samples[i - 1].position
                    : _pt.clone().add(_pt.clone().sub(samples[Math.min(i + 1, sampleCount - 1)].position)),
            )
            _nextPt.copy(
                i < sampleCount - 1
                    ? samples[i + 1].position
                    : _pt.clone().add(_pt.clone().sub(samples[Math.max(i - 1, 0)].position)),
            )
            _dir.subVectors(_prevPt, _nextPt)
            if (_dir.lengthSq() < 1e-6) _dir.set(0, 0, -1)
            else _dir.normalize()
            _upVec.copy(samples[i].up).normalize()
            _ribbonSide.crossVectors(_upVec, _dir)
            if (_ribbonSide.lengthSq() < 1e-6) _ribbonSide.set(1, 0, 0)
            else _ribbonSide.normalize()

            const t = cumulativeDistances[i] / safeTotalDistance
            const w = trailSettings.width + (trailSettings.endWidth - trailSettings.width) * t
            const vb = i * VERTICES_PER_SAMPLE * 3
            const ub = i * VERTICES_PER_SAMPLE * 2
            trailPositions.current[vb] = _pt.x - _ribbonSide.x * w * 0.5
            trailPositions.current[vb + 1] = _pt.y - _ribbonSide.y * w * 0.5
            trailPositions.current[vb + 2] = _pt.z - _ribbonSide.z * w * 0.5
            trailPositions.current[vb + 3] = _pt.x + _ribbonSide.x * w * 0.5
            trailPositions.current[vb + 4] = _pt.y + _ribbonSide.y * w * 0.5
            trailPositions.current[vb + 5] = _pt.z + _ribbonSide.z * w * 0.5
            trailUvs.current[ub] = 0
            trailUvs.current[ub + 1] = t
            trailUvs.current[ub + 2] = 1
            trailUvs.current[ub + 3] = t
        }
        trailGeom.attributes.position.needsUpdate = true
        trailGeom.attributes.uv.needsUpdate = true
        trailGeom.computeBoundingSphere()
        trailGeom.setDrawRange(0, (sampleCount - 1) * 6)
        trailMesh.visible = true

        const fp = samples[0].position
        const fp2 = samples[1]?.position ?? fp
        _dir.subVectors(fp, fp2)
        if (_dir.lengthSq() < 1e-6) _dir.set(0, 0, -1)
        else _dir.normalize()
        _upVec.copy(samples[0].up).normalize()
        _ribbonSide.crossVectors(_upVec, _dir)
        if (_ribbonSide.lengthSq() < 1e-6) _ribbonSide.set(1, 0, 0)
        else _ribbonSide.normalize()

        const r = trailSettings.frontCapRadius
        const frontLeft = {
            x: trailPositions.current[0],
            y: trailPositions.current[1],
            z: trailPositions.current[2],
            u: 0,
        }
        const frontRight = {
            x: trailPositions.current[3],
            y: trailPositions.current[4],
            z: trailPositions.current[5],
            u: 1,
        }
        const arcPointCount = FRONT_CAP_SEGMENTS + 1
        const arcPoints = Array.from({ length: arcPointCount }, (_, index) => {
            const a = -Math.PI * 0.5 + (index / FRONT_CAP_SEGMENTS) * Math.PI
            return {
                x: fp.x + _ribbonSide.x * Math.sin(a) * r + _dir.x * Math.cos(a) * r,
                y: fp.y + _dir.y * Math.cos(a) * r + _ribbonSide.y * Math.sin(a) * r,
                z: fp.z + _ribbonSide.z * Math.sin(a) * r + _dir.z * Math.cos(a) * r,
                u: index / FRONT_CAP_SEGMENTS,
            }
        })
        arcPoints[0] = frontLeft
        arcPoints[FRONT_CAP_SEGMENTS] = frontRight
        const poleIndex = Math.floor(FRONT_CAP_SEGMENTS * 0.5)
        const pole = arcPoints[poleIndex]
        let triangleIndex = 0

        const writeCapTriangle = (
            p0: { x: number; y: number; z: number; u: number },
            p1: { x: number; y: number; z: number; u: number },
        ) => {
            const vertexBase = triangleIndex * 9
            const uvBase = triangleIndex * 6
            triangleIndex += 1

            frontCapPositions.current[vertexBase] = pole.x
            frontCapPositions.current[vertexBase + 1] = pole.y
            frontCapPositions.current[vertexBase + 2] = pole.z
            frontCapPositions.current[vertexBase + 3] = p0.x
            frontCapPositions.current[vertexBase + 4] = p0.y
            frontCapPositions.current[vertexBase + 5] = p0.z
            frontCapPositions.current[vertexBase + 6] = p1.x
            frontCapPositions.current[vertexBase + 7] = p1.y
            frontCapPositions.current[vertexBase + 8] = p1.z

            // Use the real front-most perimeter vertex as the shared convergence pole.
            frontCapUvs.current[uvBase] = pole.u
            frontCapUvs.current[uvBase + 1] = 0
            frontCapUvs.current[uvBase + 2] = p0.u
            frontCapUvs.current[uvBase + 3] = 1
            frontCapUvs.current[uvBase + 4] = p1.u
            frontCapUvs.current[uvBase + 5] = 1
        }

        for (let i = 0; i < poleIndex - 1; i++) {
            writeCapTriangle(arcPoints[i], arcPoints[i + 1])
        }

        for (let i = poleIndex + 1; i < FRONT_CAP_SEGMENTS; i++) {
            writeCapTriangle(arcPoints[i], arcPoints[i + 1])
        }

        // Close the remaining area against the trail edge so the front-pole cap stays solid.
        writeCapTriangle(arcPoints[FRONT_CAP_SEGMENTS], arcPoints[0])
        capGeom.attributes.position.needsUpdate = true
        capGeom.attributes.uv.needsUpdate = true
        capGeom.computeBoundingSphere()
        capMesh.visible = r > 0
    }

    useFrame((_, delta) => {
        const safeDelta = Math.min(delta, 1 / 60)
        const timeline = timelineRef.current
        const showConfiguratorEffectPreview =
            store.appFlowPhase === 'configurator' &&
            store.configuratorActiveTab === 'effect'
        if (!timeline.isActive) {
            const samples = samplesRef.current
            if (samples.length > 0) {
                samples.pop()
                syncRibbon()
            }
            if (samples.length === 0) {
                if (trailMeshRef.current) trailMeshRef.current.visible = false
                if (frontCapMeshRef.current) frontCapMeshRef.current.visible = false
            }
            currentTrailLengthRef.current = 0
            return
        }

        if (!anchorRef.current) return
        anchorRef.current.getWorldPosition(_worldPos)
        anchorRef.current.getWorldQuaternion(_trailWorldQuat)
        _targetPos.copy(_trailOffset).applyQuaternion(_trailWorldQuat).add(_worldPos)
        _upVec.set(0, 1, 0).applyQuaternion(_trailWorldQuat).normalize()

        const targetTrailLength = (timeline.footstepsEnabled || timeline.isJumping)
            ? trailSettings.trailLength * (
                showConfiguratorEffectPreview
                    ? configuratorPreviewTrailLengthMultiplier
                    : 1
            )
            : 0

        currentTrailLengthRef.current = MathUtils.damp(
            currentTrailLengthRef.current,
            targetTrailLength,
            trailSettings.trailLengthSmoothing,
            safeDelta,
        )

        if (showConfiguratorEffectPreview) {
            _smoothedPos.copy(_targetPos)
            _previewForward.set(0, 0, -1).applyQuaternion(_trailWorldQuat).normalize()
            _previewBackward.copy(_previewForward).multiplyScalar(-1)

            const samples = samplesRef.current
            const maxPoints = Math.max(2, Math.min(MAX_SAMPLES, trailSettings.maxPoints))
            const visibleTrailLength = Math.max(currentTrailLengthRef.current, 0)
            const segmentLength = maxPoints > 1
                ? visibleTrailLength / (maxPoints - 1)
                : 0

            samples.length = maxPoints
            for (let index = 0; index < maxPoints; index += 1) {
                const distance = segmentLength * index
                const position = _smoothedPos.clone().addScaledVector(_previewBackward, distance)
                const existingSample = samples[index]

                if (existingSample) {
                    existingSample.distance = visibleTrailLength - distance
                    existingSample.position.copy(position)
                    existingSample.up.copy(_upVec)
                } else {
                    samples[index] = {
                        distance: visibleTrailLength - distance,
                        position,
                        up: _upVec.clone(),
                    }
                }
            }

            syncRibbon()
            return
        }

        if (!smoothedPosInitRef.current) {
            _smoothedPos.copy(_targetPos)
            travelDistanceRef.current = 0
            currentTrailLengthRef.current = timeline.footstepsEnabled
                ? trailSettings.trailLength
                : 0
            smoothedPosInitRef.current = true
        } else {
            _segment.subVectors(_targetPos, _smoothedPos)
            travelDistanceRef.current += _segment.length()
            _smoothedPos.lerp(
                _targetPos,
                1 - Math.exp(-trailSettings.positionSmoothing * safeDelta),
            )
        }

        const samples = samplesRef.current
        const maxPoints = Math.max(2, Math.min(MAX_SAMPLES, trailSettings.maxPoints))
        if (samples.length === 0) {
            samples.push({
                distance: travelDistanceRef.current,
                position: _smoothedPos.clone(),
                up: _upVec.clone(),
            })
        } else {
            samples[0].position.copy(_smoothedPos)
            samples[0].distance = travelDistanceRef.current
            samples[0].up.copy(_upVec)
            if (samples.length < 2) {
                samples.push({
                    distance: travelDistanceRef.current,
                    position: _smoothedPos.clone(),
                    up: _upVec.clone(),
                })
            } else {
                _prevSample.copy(samples[1].position)
                if (_prevSample.distanceToSquared(_smoothedPos) >= trailSettings.sampleSpacing ** 2) {
                    samples.splice(1, 0, {
                        distance: travelDistanceRef.current,
                        position: _smoothedPos.clone(),
                        up: _upVec.clone(),
                    })
                }
            }
            if (samples.length > maxPoints) samples.length = maxPoints
        }

        const headDistance = samples[0]?.distance ?? travelDistanceRef.current
        while (samples.length > 2) {
            const tailDistance = headDistance - samples[samples.length - 1].distance
            if (tailDistance <= currentTrailLengthRef.current + trailSettings.sampleSpacing) {
                break
            }
            samples.pop()
        }

        syncRibbon()
    })

    return null
}
